import {
  Divider,
  FormControl,
  FormLabel,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import noop from "lodash/noop";
import type { FC } from "react";
import { usePackageWithId } from "../../admin/packages/usePackageWithId";
import type { UserRole } from "../../auth/auth";
import { AssociatedFilesView } from "../../components/associatedFiles/AssociatedFilesView";
import CurrencyPicker from "../../components/currencyPicker/CurrencyPicker";
import DatePicker from "../../components/datePicker/DatePicker";
import { InfoEdit } from "../../components/editDialog/InfoEdit";
import NumberField from "../../components/numberField/NumberField";
import type { Currency } from "../../data/BaseEntity";
import type { SavedCalculationVersion } from "../calculationTypes";
import ReadonlyCalculationProducts from "./ReadonlyCalculationProducts";

interface PackageViewProps {
  readonly packageId: string;
}

const PackageView: FC<PackageViewProps> = ({ packageId }) => {
  const { data: selectedPackage } = usePackageWithId(packageId);
  const packageAttachments = selectedPackage?.files.map((f) => ({ ...f, name: "Opis pakietu" })).slice(0, 1) ?? [];
  const note = selectedPackage?.note;
  return (
    <>
      <Grid size={{ xs: 7, lg: 6 }}>
        <TextField label="Pakiet" value={selectedPackage?.name.value ?? ""} fullWidth disabled />
      </Grid>
      {packageAttachments.length > 0 && (
        <Grid
          size={2}
          container
          sx={{
            alignItems: "center",
          }}
        >
          <AssociatedFilesView values={packageAttachments} />
        </Grid>
      )}
      {note && (
        <Grid size={12}>
          <Typography variant="caption">Informacje dotyczące pakietu</Typography>
          <Typography variant="body2">{note}</Typography>
        </Grid>
      )}
    </>
  );
};

interface ProductsViewProps {
  readonly currency: Currency;
  readonly margin: number;
  readonly isCustomer: boolean;
}

const ProductsView: FC<ProductsViewProps> = ({ currency, margin, isCustomer }) => (
  <>
    <Grid size={{ xs: 4, lg: 2 }}>
      <FormControl>
        <FormLabel>Waluta</FormLabel>
        <CurrencyPicker value={currency} disabled onChange={noop} />
      </FormControl>
    </Grid>
    {!isCustomer && (
      <Grid size={{ xs: 3, lg: 2 }}>
        <NumberField label="Marża (%)" value={margin} disabled onChange={noop} />
      </Grid>
    )}
  </>
);

enum Mode {
  PACKAGE,
  PRODUCTS,
}

interface Props {
  readonly calculationVersion: SavedCalculationVersion;
  readonly role: UserRole | undefined;
}

const CalculationVersionView: FC<Props> = ({ calculationVersion, role }) => {
  const mode = !calculationVersion.packageId ? Mode.PRODUCTS : Mode.PACKAGE;
  const isCustomer = role === "CUSTOMER";
  return (
    <Grid size={12} container spacing={2}>
      <Grid size={{ xs: 6, lg: 4 }}>
        <TextField
          label="Nazwa"
          value={calculationVersion.name}
          fullWidth
          autoFocus
          onChange={noop}
          slotProps={{ input: { readOnly: true } }}
        />
      </Grid>
      <Grid size={{ xs: 6, lg: 4 }}>
        <DatePicker label="Data rozpoczęcia" readonly fullWidth value={calculationVersion.startDate} onChange={noop} />
      </Grid>
      <Grid size={{ xs: 3, lg: 2 }}>
        <NumberField
          label="Liczba osób płatnych"
          fullWidth
          readonly
          value={calculationVersion.participantsNumber.numberOfPaid}
          onChange={noop}
        />
      </Grid>
      <Grid size={{ xs: 3, lg: 2 }}>
        <NumberField
          label="Liczba osób obsługi"
          fullWidth
          readonly
          value={calculationVersion.participantsNumber.numberOfStaff}
          onChange={noop}
        />
      </Grid>
      <Grid size={12}>
        <InfoEdit label="Dodatkowe informacje" value={calculationVersion.note} readonly onChange={noop} />
      </Grid>
      <Grid size={12}>
        <Divider />
      </Grid>
      {!isCustomer && (
        <Grid size={{ xs: 3, lg: 2 }}>
          <ToggleButtonGroup exclusive value={mode} disabled>
            <ToggleButton value={Mode.PACKAGE}>Pakiet</ToggleButton>
            <ToggleButton value={Mode.PRODUCTS}>Produkty</ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      )}
      {mode === Mode.PACKAGE && <PackageView packageId={calculationVersion.packageId!} />}
      {mode === Mode.PRODUCTS && (
        <ProductsView
          isCustomer={isCustomer}
          currency={calculationVersion.currency}
          margin={calculationVersion.margin ?? 0}
        />
      )}
      <Grid size={12}>
        <ReadonlyCalculationProducts
          hideProductPrices={isCustomer && mode === Mode.PACKAGE}
          selectedProducts={calculationVersion.products}
          currency={calculationVersion.currency}
          margin={calculationVersion.margin ?? 0}
          price={calculationVersion.price}
          participantsNumber={
            calculationVersion.participantsNumber.numberOfPaid + calculationVersion.participantsNumber.numberOfStaff
          }
        />
      </Grid>
    </Grid>
  );
};

export default CalculationVersionView;
